/**
 * Todesritter — Pastinake "Mortinak" (Blut-Bruiser). LMAO-Style (BATTLE-17):
 * blasse Knochen-Wurzel, massiv-vertikal wie ein gezogener Knochen-Dolch —
 * breite Schultern, konkav zulaufende Klingen-Spitze, quer laufende
 * Ring-Kerben (Wurzelringe = Knochen-Look) und ein kraenkelnd-sparsamer
 * Blattschopf, dessen Spitzen frostig vereist sind (Eis-Akzent docs/13 §6).
 * 2★: Raureif-Kante + Eiszapfen am Kragen-Ring.
 * 3★: frostiger Schimmer + Eiskristall-Funkeln (reagieren auf pose.castPulse).
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, specular, volumeGradient } from '../draw3d'
import { KEYWORD_COLORS } from '../../ui/theme'

/** verbindlicher Eis-Akzent (Brief + docs/13 §6) — nur Frost-Deko, nie Koerper-Grundton */
const ICE = KEYWORD_COLORS.ice // #5bd8f0

export const paintPastinake: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)
  // kraenkelndes Blattwerk: ausgetrockneter Oliv-Ton, aus dem Knochen-Beige abgeleitet
  const leafBase = darken(colors.bodyDark, 16)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.translate(x, y)

  // --- Blattschopf: sparsame, kraenkelnd haengende Blaetter (hinter dem Korpus) ---
  // Jedes Blatt: Basis-Oliv -> klar vereister Frost-Tip (Gradient entlang des Blatts)
  const blade = (
    bx: number, by: number, cx: number, cy: number, tx: number, ty: number, w: number,
  ): void => {
    const g = ctx.createLinearGradient(bx * r, by * r, tx * r, ty * r)
    g.addColorStop(0, leafBase)
    g.addColorStop(0.5, lighten(leafBase, 10))
    g.addColorStop(0.75, dead ? colors.bodyDark : darken(ICE, 12))
    g.addColorStop(1, dead ? colors.bodyDark : lighten(ICE, 22))
    ctx.fillStyle = g
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.2, r * 0.042)
    ctx.beginPath()
    ctx.moveTo((bx - w) * r, by * r)
    ctx.quadraticCurveTo((cx - w * 0.7) * r, (cy - w * 0.5) * r, tx * r, ty * r)
    ctx.quadraticCurveTo((cx + w * 1.2) * r, (cy + w * 1.1) * r, (bx + w) * r, by * r)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // Blattader als kalte Licht-Linie
    if (!dead) {
      ctx.strokeStyle = rgba(lighten(ICE, 10), 0.55)
      ctx.lineWidth = Math.max(1, r * 0.028)
      ctx.beginPath()
      ctx.moveTo(bx * r, (by - 0.04) * r)
      ctx.quadraticCurveTo(cx * r, (cy + w * 0.3) * r, tx * r, ty * r)
      ctx.stroke()
      glow(ctx, tx * r, ty * r, r * 0.13, ICE, 0.2 + pose.castPulse * 0.3)
    }
  }
  blade(-0.2, -0.88, -0.6, -1.36, -0.86, -1.04, 0.13) // links: hoch + abgeknickt haengend
  blade(0.0, -0.92, -0.14, -1.64, 0.2, -1.5, 0.15) // mitte: hoechstes Blatt, Spitze kippt
  blade(0.18, -0.86, 0.44, -1.26, 0.62, -1.0, 0.11) // rechts: kurz, muede haengend

  // --- Seitenwurzel-Krallen (hinter dem Korpus) — Wurzel-Charakter, boese Silhouette ---
  ctx.fillStyle = darken(colors.body, 8)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW * 0.75
  ctx.beginPath()
  ctx.moveTo(-r * 0.34, r * 0.26)
  ctx.quadraticCurveTo(-r * 0.66, r * 0.4, -r * 0.62, r * 0.82)
  ctx.quadraticCurveTo(-r * 0.44, r * 0.56, -r * 0.2, r * 0.5)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(r * 0.26, r * 0.42)
  ctx.quadraticCurveTo(r * 0.56, r * 0.56, r * 0.5, r * 0.92)
  ctx.quadraticCurveTo(r * 0.34, r * 0.64, r * 0.14, r * 0.62)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()

  // --- Korpus: breite Schultern, konkaver Klingen-Taper in die Spitze ---
  const bodyPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(0, -r * 1.0)
    ctx.bezierCurveTo(r * 0.42, -r * 1.0, r * 0.72, -r * 0.86, r * 0.78, -r * 0.5)
    ctx.bezierCurveTo(r * 0.82, -r * 0.12, r * 0.6, r * 0.28, r * 0.34, r * 0.62)
    ctx.bezierCurveTo(r * 0.16, r * 0.86, r * 0.1, r * 0.98, r * 0.03, r * 1.06)
    ctx.bezierCurveTo(-r * 0.06, r * 0.94, -r * 0.16, r * 0.78, -r * 0.3, r * 0.58)
    ctx.bezierCurveTo(-r * 0.56, r * 0.24, -r * 0.78, -r * 0.1, -r * 0.76, -r * 0.52)
    ctx.bezierCurveTo(-r * 0.72, -r * 0.88, -r * 0.4, -r * 1.0, 0, -r * 1.0)
    ctx.closePath()
  }
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.fillStyle = volumeGradient(ctx, 0, -r * 0.15, r, colors.body)
  ctx.fill()
  ctx.stroke()

  // --- Innenleben, geclippt auf den Korpus ---
  ctx.save()
  bodyPath()
  ctx.clip()
  ctx.lineCap = 'round'

  // Ring-Kerben (Wurzelringe = Knochen-Look): dunkle Furche, Licht-Lippe darunter
  // (Licht oben-links trifft die untere Kerbenkante); leichter Bogen nach unten =
  // Blick leicht von oben. Clip schneidet exakt auf die Silhouette.
  const rings: ReadonlyArray<readonly [number, number]> = [
    [-0.72, 0.74], [0.2, 0.7], [0.48, 0.52], [0.74, 0.32],
  ]
  for (const [ry, w] of rings) {
    const bow = w * 0.22
    ctx.strokeStyle = rgba(darken(colors.body, 32), 0.9)
    ctx.lineWidth = Math.max(1.4, r * 0.06)
    ctx.beginPath()
    ctx.moveTo(-w * r, ry * r)
    ctx.quadraticCurveTo(0, (ry + bow) * r, w * r, ry * r)
    ctx.stroke()
    ctx.strokeStyle = rgba(lighten(colors.body, 32), dead ? 0.3 : 0.75)
    ctx.lineWidth = Math.max(1, r * 0.035)
    ctx.beginPath()
    ctx.moveTo(-w * r, (ry + 0.055) * r)
    ctx.quadraticCurveTo(0, (ry + bow + 0.055) * r, w * r, (ry + 0.055) * r)
    ctx.stroke()
  }

  // Knochen-Risse (Blut-Bruiser: der Kerl hat was einstecken muessen)
  ctx.strokeStyle = rgba(darken(colors.body, 42), 0.7)
  ctx.lineWidth = Math.max(1, r * 0.032)
  ctx.beginPath()
  ctx.moveTo(r * 0.36, r * 0.52)
  ctx.lineTo(r * 0.24, r * 0.66)
  ctx.lineTo(r * 0.31, r * 0.8)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(-r * 0.56, -r * 0.08)
  ctx.lineTo(-r * 0.44, r * 0.04)
  ctx.stroke()

  // 3★: frostiger Schimmer — kalte diagonale Licht-Bahn ueber dem Knochen
  if (tier >= 3 && !dead) {
    const sheen = ctx.createLinearGradient(-r * 0.9, -r * 0.9, r * 0.9, r * 0.9)
    const a = 0.1 + pose.castPulse * 0.14
    sheen.addColorStop(0.22, rgba(ICE, 0))
    sheen.addColorStop(0.5, rgba(lighten(ICE, 28), a))
    sheen.addColorStop(0.78, rgba(ICE, 0))
    ctx.fillStyle = sheen
    ctx.fillRect(-r, -r * 1.1, r * 2, r * 2.3)
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Innenleben knackig nachziehen (Readability der Silhouette)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.stroke()

  // --- Kronen-Krater + abgebrochener Stiel-Stumpf (kraenkelnd) ---
  ctx.fillStyle = rgba(darken(colors.body, 24), 0.85)
  ctx.beginPath()
  ctx.ellipse(0, -r * 0.9, r * 0.2, r * 0.065, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = leafBase
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1, r * 0.04)
  ctx.beginPath()
  ctx.moveTo(-r * 0.055, -r * 0.9)
  ctx.lineTo(-r * 0.065, -r * 1.05)
  ctx.lineTo(r * 0.06, -r * 1.08)
  ctx.lineTo(r * 0.07, -r * 0.9)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
  if (!dead) {
    // vereiste Bruchkante des Stumpfs
    ctx.strokeStyle = rgba(lighten(ICE, 12), 0.9)
    ctx.lineWidth = Math.max(1, r * 0.035)
    ctx.beginPath()
    ctx.moveTo(-r * 0.06, -r * 1.05)
    ctx.lineTo(r * 0.055, -r * 1.08)
    ctx.stroke()
  }

  // --- 2★: Raureif-Kante an der Schulter + Eiszapfen-Frostbart am Kinn-Ring ---
  if (tier >= 2 && !dead) {
    ctx.strokeStyle = rgba(lighten(ICE, 22), 0.7)
    ctx.lineWidth = Math.max(1.2, r * 0.05)
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(-r * 0.68, -r * 0.42)
    ctx.quadraticCurveTo(-r * 0.68, -r * 0.85, -r * 0.16, -r * 0.97)
    ctx.stroke()
    // Eiszapfen haengen aus der unteren Kerbe (Ring bei +0.48) — klar UNTER
    // Mund und Face-Zone (Gesicht bei -0.18r bleibt frei)
    ctx.strokeStyle = rgba(darken(ICE, 22), 0.7)
    ctx.lineWidth = Math.max(1, r * 0.028)
    for (const [ix, len] of [[-0.26, 0.13], [0.0, 0.19], [0.22, 0.11]] as const) {
      ctx.fillStyle = rgba(lighten(ICE, 30), 0.85)
      ctx.beginPath()
      ctx.moveTo((ix - 0.055) * r, r * 0.52)
      ctx.lineTo((ix + 0.055) * r, r * 0.52)
      ctx.lineTo(ix * r, (0.52 + len) * r)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
    }
  }

  // --- 3★: Eiskristall-Funkeln (deterministische Positionen, Face-Zone frei) ---
  if (tier >= 3 && !dead) {
    const a = 0.65 + pose.castPulse * 0.35
    ctx.save()
    ctx.strokeStyle = rgba(lighten(ICE, 20), a)
    ctx.lineCap = 'round'
    ctx.shadowColor = ICE
    ctx.shadowBlur = r * (0.12 + pose.castPulse * 0.14)
    for (const [sx, sy, s] of [
      [-0.42, -0.72, 0.07], [0.52, -0.42, 0.055], [-0.38, 0.32, 0.05],
      [0.16, 0.72, 0.06], [0.46, 0.02, 0.045],
    ] as const) {
      ctx.lineWidth = Math.max(1, r * 0.028)
      ctx.beginPath()
      ctx.moveTo((sx - s) * r, sy * r)
      ctx.lineTo((sx + s) * r, sy * r)
      ctx.moveTo(sx * r, (sy - s) * r)
      ctx.lineTo(sx * r, (sy + s) * r)
      ctx.stroke()
    }
    ctx.restore()
    // kalter Hauch um die Klingen-Spitze
    glow(ctx, r * 0.03, r * 0.98, r * 0.34, ICE, 0.16 + pose.castPulse * 0.26)
  }

  // --- Licht: kalte Rim-Kante entlang der linken Schulter + Specular ---
  if (!dead) {
    ctx.strokeStyle = rgba(lighten(colors.body, 45), 0.6)
    ctx.lineWidth = Math.max(1.5, r * 0.07)
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(-r * 0.6, -r * 0.02)
    ctx.quadraticCurveTo(-r * 0.76, -r * 0.6, -r * 0.26, -r * 0.92)
    ctx.stroke()
    specular(ctx, -r * 0.32, -r * 0.62, r * 0.13, 0.45)
  }

  ctx.restore()
}
